import { IComponentOptions, IScope, ITimeoutService, noop, IPromise, IQService } from "angular";
import { HotkeysProvider } from "angular-hotkeys";
import dayjs from "@gtmhub/dayjs";
import { WhiteboardRenderer, IWhiteboardRendererContext, whiteboardRendererFactory, viewportMargin } from "../rendering/whiteboard-renderer";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { GtmhubController } from "@gtmhub/core";
import { unique, toIdMap, splitBy, IdMap, NameMap, flatten } from "@gtmhub/util";
import { IWhiteboard, IWhiteboardState, ICollaborator, DraftToImportedGoalMap, IReactionSettings, IChangeableWhiteboard } from "../models";
import { IShapeContextMenuItem } from "./shape-context-menu.component";
import { FontDownloader } from "../rendering/font-downloader";
import { OkrEvents, GoalsChangedEventArgs } from "@gtmhub/okrs";
import {
  IWhiteboardShapeState,
  IAsyncState,
  cloneShape,
  WhiteboardShapeStateType,
  IPosition,
  INoteState,
  IDraftGoalState,
  IShapeReactionResponse,
  WhiteboardShape,
} from "../rendering/shapes";
import { WhiteboardStateHolder } from "../rendering/state/whiteboard-state-holder";
import { ShapeStateConverter } from "../rendering/state/shape-state-converter";
import { WhiteboardsService } from "../whiteboards-service";
import { IIndicator, UIErrorHandlingService, IUIError } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { WhiteboardsClipboard, offsetShape } from "../whiteboards-clipboard";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { localize } from "@gtmhub/localization";
import { IWhiteboardLinkState } from "../rendering/links";
import { PermissionsService, getCurrentUserId, UserService, IUser } from "@gtmhub/users";
import { WhiteboardEvents } from "../events";
import { IAllowedActionsMap } from "@gtmhub/sessions/models/models";
import { WhiteboardsUserSettingsService } from "../whiteboards-user-settings-service";
import {
  ICollaborateWhiteboardSocketMessage,
  IShapeLockedSocketMessage,
  IShapeUnlockedSocketMessage,
  IStateChangeSocketMessage,
  IStateChangeSuccess,
  WhiteboardSocketService,
} from "../whiteboard-socket-service";
import { IHasPageTitle, NavigationEvents } from "@gtmhub/navigation";
import { ChangeHistoryTracker } from "../rendering/state/change-history-tracker";
import { StateProcessor } from "../rendering/state/state-processor";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { IZoomOptions } from "./whiteboard-minimap.component";
import { getRandomColor, px } from "../rendering/util";
import { MinimapRenderer } from "../rendering/whiteboard-minimap-renderer";
import { MultiWhiteboardRenderer } from "../rendering/whiteboard-multi-renderer";
import { hideUsersListTooltip, showUsersListTooltip } from "../rendering/behaviors/users-list-tooltip";
import { FileService } from "@gtmhub/files";
import { createNewImage, createNewNote, onStateChangeAddShape, onStateChangeRemoveShape, publishUnsyncedStateChanges } from "./utils";
import colors from "wwwroot/json/colors.json";
import { WhiteboardImageService } from "../whiteboard-image-service";
import { v4 as uuidv4 } from "uuid";
import { IOriginalImageProps } from "../rendering/shapes/image";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { StateService } from "@uirouter/angularjs";
import { whiteboardsState } from "../whiteboards.states";
import { BufferedStateChangeNotifier } from "../rendering/state/buffered-state-change-notifier";
import { AssigneeService } from "@gtmhub/assignees/services";
import { ICreateReaction } from "@gtmhub/experiments/models/models";
import { ReactionService } from "@gtmhub/experiments/services/reaction.service";
import { IWhiteboardStateChange } from "../rendering/state/state-change-notifier";
import { FeatureFlag } from "../../featureToggle/models";
import { MaxReactionsPerUserAllowed } from "../rendering/behaviors/voting";
import { GoalService } from "@gtmhub/goals/goal.service";
import { IGoalChildReorderPayload } from "@gtmhub/goals/models";
import { IReactedUser } from "./tooltip.component";
import { getClosestOpenActiveSession, getClosestOpenFutureSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { AlignmentRejectedMessageModal } from "./message-modal.component";
import { ViewHistoryService } from "@gtmhub/view-history";
import { navItemsFactory } from "@webapp/navigation/utils/nav-items.util";

interface IWhiteboardComponentBindings {
  whiteboardId: string;
}

const bindStateToCtrl = (state: IAssigneesStoreState & ISessionsStoreState) => {
  return {
    sessionsIdMap: state.sessions.map,
    sessions: state.sessions.items,
    assigneeIdMap: state.assignees.map,
    reduxItemsFetched: state.assignees.isFetched && state.sessions.isFetched,
    reduxError: state.assignees.error || state.sessions.error,
  };
};

export interface WhiteboardComponentCtrl extends ReturnType<typeof bindStateToCtrl>, IWhiteboardComponentBindings {}

export class WhiteboardComponentCtrl extends GtmhubController implements IHasPageTitle {
  indicators: {
    loading?: IIndicator;
  };
  whiteboard?: IWhiteboard;
  shapeContextMenuItems: IShapeContextMenuItem[];
  tooltipItems: IReactedUser[];
  currentUserAllowedActions: (keyof IAllowedActionsMap)[];
  collaborators: IdMap<ICollaborator>;

  viewport = (): IPosition => this.whiteboardRenderer.viewportPosition;
  canvasDataUrl = (): string => this.whiteboardRenderer.toDataURL();
  zoom: number;
  hoverIds: string[];

  private whiteboardRenderer: WhiteboardRenderer;
  private minimapRenderer: MinimapRenderer;
  private multiRenderer: MultiWhiteboardRenderer;
  private bufferedStateChangeNotifier: BufferedStateChangeNotifier;
  private selectedShapes: IWhiteboardShapeState[] = [];
  private context: Omit<IWhiteboardRendererContext, "assignees">;
  private currentUserConnectionId: string;
  private changeTracker: ChangeHistoryTracker;
  private stateProcessor: StateProcessor;
  private whiteboardEvents: WhiteboardEvents;
  private socketIndicators: {
    isSocketInitialized?: boolean;
    isSocketReconnecting?: boolean;
  };
  private lockedShapesIdMap: NameMap<string> = {};
  private currentUser: IUser;

  static $inject = [
    "$scope",
    "$ngRedux",
    "$timeout",
    "$q",
    "$state",
    "hotkeys",
    "AssigneeActions",
    "PlanningSessionsActions",
    "ShapeStateConverter",
    "WhiteboardsService",
    "FontDownloader",
    "WhiteboardsClipboard",
    "WhiteboardStateHolder",
    "UIErrorHandlingService",
    "PermissionsService",
    "WhiteboardsUserSettingsService",
    "WhiteboardSocketService",
    "AnalyticsService",
    "FileService",
    "WhiteboardImageService",
    "ReactionService",
    "$rootScope",
    "AssigneeService",
    "FeatureToggleService",
    "GoalService",
    "UserService",
    "AlignmentRejectedMessageModal",
    "ViewHistoryService",
  ];

  constructor(
    private $scope: IScope & IExtendedRootScopeService,
    $ngRedux: INgRedux,
    private $timeout: ITimeoutService,
    private $q: IQService,
    private $state: StateService,
    hotkeys: HotkeysProvider,
    assigneeActions: AssigneeActions,
    planningSessionsActions: PlanningSessionsActions,
    private shapeStateConverter: ShapeStateConverter,
    private whiteboardsService: WhiteboardsService,
    private fontDownloader: FontDownloader,
    private whiteboardsClipboard: WhiteboardsClipboard,
    private whiteboardStateHolder: WhiteboardStateHolder,
    private uiErrorHandlingService: UIErrorHandlingService,
    private permissionsService: PermissionsService,
    private whiteboardsUserSettingsService: WhiteboardsUserSettingsService,
    private whiteboardSocketService: WhiteboardSocketService,
    private analyticsService: AnalyticsService,
    private fileService: FileService,
    private whiteboardImageService: WhiteboardImageService,
    private reactionService: ReactionService,
    private $rootScope: ITraceRootScopeService,
    private assigneeService: AssigneeService,
    private featureToggleService: FeatureToggleService,
    private goalService: GoalService,
    private userService: UserService,
    private alignmentRejectionModal: AlignmentRejectedMessageModal,
    private viewHistoryService: ViewHistoryService
  ) {
    super();

    this.indicators = {};
    this.socketIndicators = {};
    this.shapeContextMenuItems = [];
    this.tooltipItems = [];
    this.context = {
      readonly: false,
      hoverAssigneeIds: (ids: string[]) => this.hoverAssigneeIds(ids),
      generateFileUrl: (url: string) => this.fileService.generateFileUrl(url),
      createReaction: (createReaction: ICreateReaction) => this.reactionService.createReaction(createReaction),
      deleteReaction: (reactionName: string, targetId: string) => this.reactionService.deleteReaction(reactionName, targetId),
      showErrorModal: (error: IUIError) => this.uiErrorHandlingService.handleModal(error),
      whiteboardId: "",
      setPreventDblClick: (prevent: boolean) => this.reactionService.setPreventDblClick(prevent),
      getPreventDblClick: () => this.reactionService.getPreventDblClick(),
    };

    const okrEvents = new OkrEvents($scope);
    okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.whiteboardEvents = new WhiteboardEvents($scope);
    this.whiteboardEvents.onDraftUpdated((args) =>
      this.onShapeStateChange(args.id, args.changes, {
        undo: true,
        notify: true,
      })
    );
    this.whiteboardEvents.onDraftsPublished((args) => this.onDraftsPublished(args));
    this.whiteboardEvents.onCollaborate((args) => this.onCollaborate(args));
    this.whiteboardEvents.onForeignStateChange((args) => this.onForeignStateChange(args));
    this.whiteboardEvents.onShapeLocked((args) => this.onShapeLocked(args));
    this.whiteboardEvents.onShapeUnlocked((args) => this.onShapeUnlocked(args));
    this.whiteboardEvents.onSocketError(() => this.onSocketError());
    this.whiteboardEvents.onSocketOpened(() => this.onSocketOpened());
    this.whiteboardEvents.onReactionSettingsChanged((args) => this.onReactionSettingsChanged(args.payload));
    this.whiteboardEvents.onShapeReaction((args) => this.onShapeReaction(args.payload));
    this.whiteboardEvents.onLockWhiteboard(() => (this.context.readonly = true));
    this.whiteboardEvents.onUnlockSelectedShapes((args) => this.onUnlockSelectedShapes(args));
    this.whiteboardEvents.onWhiteboardPropertyChanged((args) => this.onDefaultSessionChanged(args.payload));

    this.context.events = this.whiteboardEvents;

    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(assigneeActions.getAssignees());
    $ngRedux.dispatch(planningSessionsActions.getSessionsIfMissing());

    this.configureHotkeys(hotkeys);

    const resizeHandler = () => {
      if (this.whiteboardRenderer) {
        this.whiteboardRenderer.resize();
        this.renderMinimap();
      }
    };

    const showTooltipHandler = (e) => {
      const tooltipEl = this.toggleTooltip(true);

      this.tooltipItems = e.detail.users;

      const { x, y } = e.detail.shape.getAbsolutePosition();

      tooltipEl.style.top = px(y + e.detail.shape.height());
      tooltipEl.style.left = px(x);
    };

    const navEvents = new NavigationEvents($scope);
    navEvents.onNavigationToggled(resizeHandler);

    window.addEventListener("resize", resizeHandler);
    window.addEventListener("show-tooltip", showTooltipHandler);

    this.onDestroy(() => {
      window.removeEventListener("resize", resizeHandler);
      window.removeEventListener("show-tooltip", showTooltipHandler);
    });

    this.multiRenderer = new MultiWhiteboardRenderer();
    this.bufferedStateChangeNotifier = new BufferedStateChangeNotifier(this.whiteboardSocketService);
  }

  hideTooltip() {
    this.toggleTooltip(false);
  }

  private toggleTooltip(show = false): HTMLElement {
    const tooltipEl = document.getElementById("tooltip");
    if (!tooltipEl) {
      throw new Error("Cannot find reaction tooltip element");
    }
    tooltipEl.style.visibility = show ? "visible" : "hidden";
    return tooltipEl;
  }

  $onInit(): void {
    this.indicators.loading = { progress: true };

    const whiteboardPromise = this.loadWhiteboard();
    const statePromise = this.$scope.watchUntilAndRejectOnError(
      () => this.reduxItemsFetched,
      () => this.reduxError
    );
    const currentUserAllowedActionsPromise = this.getCurrentUserAllowedActions();

    this.context.whiteboardId = this.whiteboardId;

    const whiteboardVotingPromise = this.featureToggleService
      .isFeatureAvailable(FeatureFlag.FeatureWhiteboardVoting)
      .then((enabled) => (this.context.whiteboardVotingEnabled = enabled));

    const whiteboardBasicShapesPromise = this.featureToggleService
      .isFeatureAvailable(FeatureFlag.FeatureWhiteboardBasicShapes)
      .then((enabled) => (this.context.whiteboardBasicShapesEnabled = enabled));

    const whiteboardSessionManagementPromise = this.featureToggleService
      .isFeatureAvailable(FeatureFlag.WhiteboardsSessionManagement)
      .then((enabled) => (this.context.whiteboardSessionManagementEnabled = enabled));

    const loadingPromise = this.render(
      this.$q
        .all([
          whiteboardPromise,
          statePromise,
          currentUserAllowedActionsPromise,
          whiteboardVotingPromise,
          whiteboardBasicShapesPromise,
          whiteboardSessionManagementPromise,
        ])
        .then((result) => result[0])
    );

    loadingPromise.then(
      () => delete this.indicators.loading,
      (error) => {
        this.$state.go(whiteboardsState.name).then(() => this.uiErrorHandlingService.handleModal(error));
      }
    );
  }

  private onSocketError(): void {
    this.socketIndicators.isSocketReconnecting = true;
    this.collaborators = null;
  }

  private onSocketOpened(): void {
    if (this.socketIndicators.isSocketReconnecting) {
      this.whiteboardStateHolder.unload();
      this.$onInit();
      this.socketIndicators.isSocketReconnecting = false;
    }
  }

  imageDropped(url: string, rawImageProps: IOriginalImageProps): IPromise<void> {
    const position = {
      x: this.viewport().x + viewportMargin.leftRight,
      y: this.viewport().y + viewportMargin.topBottom,
    };

    const imageShapeId = uuidv4();
    return this.whiteboardImageService
      .uploadFromDevice(this.whiteboardId, imageShapeId, url, rawImageProps)
      .then((imageUrl) => {
        const renderingSize = this.whiteboardImageService.calculateRenderingSize(rawImageProps);
        const rawImage = {
          name: rawImageProps.name,
          size: rawImageProps.size,
          type: rawImageProps.type,
          width: rawImageProps.width,
          height: rawImageProps.height,
        };
        const zIndex = this.whiteboardStateHolder.calculateNextZIndex();
        const shape = createNewImage({
          position,
          createdById: getCurrentUserId(),
          imageShapeId,
          imageUrl,
          renderingSize,
          rawImage,
          zIndex,
          rotationAngle: 0,
        });

        this.analyticsService.track("Whiteboard Image Dropped");

        this.addShape(shape);
      })
      .catch(() => this.uiErrorHandlingService.handleModal({ body: localize("something_went_wrong") }));
  }

  pageTitle(): string {
    return this.whiteboard && this.whiteboard.name;
  }

  shapeContextMenuStateChange(changes: Record<string, unknown>): void {
    if (this.selectedShapes.length === 1) {
      const selectedShapeId = this.selectedShapes[0].id;
      if (changes.zIndex !== undefined) {
        this.stateProcessor.updateShapesZIndex(selectedShapeId, changes.zIndex as number);
        this.reRenderAndUpdateSelectedShape(selectedShapeId);
      } else {
        this.onShapeStateChange(selectedShapeId, changes, { undo: true, notify: true });
      }
    }
  }

  addShape(shape: IWhiteboardShapeState): void {
    this.stateProcessor.addShape(shape);
    this.whiteboardStateHolder.render(this.multiRenderer);
  }

  onDoubleClick(event: MouseEvent): void {
    if (this.context.getPreventDblClick()) {
      this.context.setPreventDblClick(false);
      return;
    }

    // Do not process the Double Click event if it is over a shape
    if (this.selectedShapes.length != 0 || this.context.readonly) {
      return;
    }

    const zIndex = this.whiteboardStateHolder.calculateNextZIndex();
    const shape: INoteState = createNewNote(null, getCurrentUserId(), zIndex);
    const viewportPosition = this.whiteboardRenderer.viewportPosition;
    const cursorPosition: IPosition = {
      x: event.offsetX / this.whiteboardRenderer.getScale().x + viewportPosition.x - shape.size.width / 2,
      y: event.offsetY / this.whiteboardRenderer.getScale().y + viewportPosition.y - shape.size.height / 2,
    };
    shape.position = cursorPosition;

    this.stateProcessor.addShape(shape);
    this.whiteboardStateHolder.render(this.multiRenderer);
  }

  changeZoom(options: IZoomOptions): void {
    this.$rootScope.traceAction("change_zoom", () => {
      this.zoom = options.zoom;
      this.whiteboardRenderer.zoom(this.zoom / 100, { relativeToMouse: options.relativeToMouse });
      this.whiteboardsUserSettingsService.update(this.whiteboardId, { zoom: this.zoom });
      this.renderMinimap();
    });
  }

  fitToScreen(): void {
    this.$rootScope.traceAction("fit_to_screen", () => {
      const zoom = this.whiteboardRenderer.fitToScreen();
      this.zoom = zoom;
      this.whiteboardsUserSettingsService.update(this.whiteboardId, { zoom: this.zoom });
      this.renderMinimap();
    });
  }

  getDefaultSessionId(): IPromise<string> {
    return this.getCurrentUser().then(
      (user) => {
        this.currentUser = user;

        if (this.context.whiteboardSessionManagementEnabled) {
          if (!this.whiteboard.defaultSessionId) {
            return this.setDefaultSession();
          }

          return this.$q.resolve(this.whiteboard.defaultSessionId);
        }

        return getClosestOpenActiveSession(Object.values(this.sessions), this.currentUser)?.id || "";
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
        return "";
      }
    );
  }

  private getCurrentUser(): IPromise<IUser> {
    if (!this.currentUser) {
      return this.userService.getUser(getCurrentUserId());
    }

    return this.$q.resolve(this.currentUser);
  }

  private onReactionSettingsChanged(settings: IReactionSettings): void {
    if (this.whiteboard.reactionSettings.isActive != settings.isActive) {
      if (settings.isActive) {
        this.whiteboardEvents.broadcastReactionsEnabled();
      } else {
        this.whiteboardEvents.broadcastReactionsDisabled();
      }
    }

    if (this.whiteboard.reactionSettings.isOneReactionPerItem != settings.isOneReactionPerItem) {
      this.whiteboardEvents.broadcastMaxReactionsPerUserAllowedChanged(
        settings.isOneReactionPerItem ? MaxReactionsPerUserAllowed.RESTRICTED : MaxReactionsPerUserAllowed.DEFAULT
      );
    }

    this.whiteboard.reactionSettings = settings;
    this.whiteboardRenderer.updateReactionSettingsInContext(settings);
  }

  private onDefaultSessionChanged(changes: IChangeableWhiteboard): void {
    if (this.whiteboard.defaultSessionId !== changes.defaultSessionId && this.context.whiteboardSessionManagementEnabled) {
      this.whiteboard = {
        ...this.whiteboard,
        defaultSessionId: changes.defaultSessionId,
      };
    }
  }

  showUsersListTooltip = showUsersListTooltip;
  hideUsersListTooltip = hideUsersListTooltip;

  private onShapeLocked(args: IShapeLockedSocketMessage): void {
    const payload = args.payload;
    this.whiteboardRenderer.lockShapeById(payload.shapeId, this.getCollaboratorLockedShapeConfig(args.connectionId));
    this.lockedShapesIdMap[payload.shapeId] = args.connectionId;
  }

  private onShapeUnlocked(args: IShapeUnlockedSocketMessage): void {
    const payload = args.payload;
    this.whiteboardRenderer.unlockShapes(payload.shapeId);
    delete this.lockedShapesIdMap[payload.shapeId];
  }

  private onDraftsPublished(publishedMap: DraftToImportedGoalMap): void {
    this.stateProcessor.processPublishedDraft(publishedMap);
    this.whiteboardStateHolder.render(this.multiRenderer);
  }

  private configureHotkeys(hotkeys: HotkeysProvider) {
    const ctrlA = hotkeys.add({
      combo: "mod+a",
      callback: () => this.whiteboardRenderer.selectAll(),
    });

    const ctrlC = hotkeys.add({
      combo: "mod+c",
      callback: () => this.whiteboardsClipboard.copy(...this.selectedShapes),
    });

    const ctrlV = hotkeys.add({
      combo: "mod+v",
      callback: () => {
        this.changeTracker
          .beginTransaction("Paste shapes")
          .updateShape(...this.whiteboardsClipboard.paste())
          .commit();
        this.whiteboardStateHolder.render(this.multiRenderer);
      },
    });

    const ctrlZ = hotkeys.add({
      combo: "mod+z",
      callback: (event) => {
        event.preventDefault();
        if (this.changeTracker.undo()) {
          this.whiteboardStateHolder.render(this.multiRenderer);
        }
      },
    });

    const shiftCtrlZ = hotkeys.add({
      combo: "shift+mod+z",
      callback: (event) => {
        event.preventDefault();
        if (this.changeTracker.redo()) {
          this.whiteboardStateHolder.render(this.multiRenderer);
        }
      },
    });

    const del = hotkeys.add({
      combo: "del",
      callback: () => this.deleteSelectedShapes(),
    });

    this.onDestroy(() => {
      hotkeys.del(ctrlA);
      hotkeys.del(ctrlC);
      hotkeys.del(ctrlV);
      hotkeys.del(ctrlZ);
      hotkeys.del(shiftCtrlZ);
      hotkeys.del(del);
    });
  }

  private deleteSelectedShapes(): void {
    this.stateProcessor.deleteSelectedShapes(this.selectedShapes);
    this.updateShapeDependenciesOnDeletion();
    this.whiteboardStateHolder.render(this.multiRenderer);
  }

  private updateShapeDependenciesOnDeletion(): void {
    if (this.selectedShapes.length === 1) {
      const shapeId = this.selectedShapes[0].id;
      delete this.lockedShapesIdMap[shapeId];
    }

    this.selectedShapes = [];
    this.shapeContextMenuItems = [];
  }

  private cloneSelectedShape(): void {
    this.$rootScope.traceAction("clone_selected_shape", () => {
      const clonedShape = offsetShape(cloneShape(this.selectedShapes[0]));
      if ("ownerIds" in clonedShape) {
        clonedShape.ownerIds = this.assigneeService.getActiveAssigneesOnly(clonedShape.ownerIds);
      }

      clonedShape.zIndex = this.whiteboardStateHolder.calculateNextZIndex();
      this.changeTracker.beginTransaction("Clone shape").updateShape(clonedShape).commit();
      this.whiteboardStateHolder.render(this.multiRenderer);
    });
  }

  private convertSelectedShapeToGoal(): void {
    const selectedShape = this.selectedShapes[0];
    if (selectedShape.type === "note" || selectedShape.type === "heading") {
      this.getDefaultSessionId().then((defaultSessionId) => {
        this.stateProcessor.convertToGoal(selectedShape, { sessionId: defaultSessionId });
        this.updateShapeDependenciesOnDeletion();
        this.whiteboardStateHolder.render(this.multiRenderer);
      });
    }
  }

  private initRenderer() {
    if (this.whiteboardRenderer) {
      // Renderers are already initialized
      return this.$q.resolve();
    }

    const whiteboardUserSettings = this.whiteboardsUserSettingsService.update(this.whiteboardId, { openedAt: dayjs.utc().format() });
    this.zoom = whiteboardUserSettings.zoom;

    const canvasContainer = document.getElementById("canvas-container") as HTMLDivElement;

    const context: IWhiteboardRendererContext = { ...this.context, assignees: (id: string) => this.assigneeIdMap[id] };
    this.whiteboardRenderer = whiteboardRendererFactory.create(canvasContainer, context, { zoomRatio: whiteboardUserSettings.zoom / 100 });
    this.multiRenderer.setWhiteboardRenderer = this.whiteboardRenderer;

    // Loading minimap in separate function and added recursion and timeout as sometimes the whiteboard-minimap.component
    // isn't loading on time and breaks the process
    const countTriesLoadingMinimap = 0;

    return this.$q
      .resolve(this.loadMinimap(countTriesLoadingMinimap))
      .then(() => {
        if (!this.socketIndicators.isSocketInitialized) {
          this.whiteboardSocketService.initializeSocket(this.whiteboardId);
          this.onDestroy(() => this.whiteboardSocketService.close(1000, "Component destroyed"));
          this.socketIndicators.isSocketInitialized = true;
        }
      })
      .then(() => {
        this.attachEventsToWhiteboardRenderer();
        this.onDestroy(() => this.whiteboardRenderer.destroy());
      });
  }

  private loadMinimap(loadingTries) {
    const limit = 5;
    const delay = 100;
    const minimapContainer = document.getElementById("minimap-canvas-container") as HTMLDivElement;

    if (minimapContainer) {
      this.minimapRenderer = new MinimapRenderer(minimapContainer);
      this.multiRenderer.setMinimapRenderer = this.minimapRenderer;
      this.minimapRenderer.addEventListener("moveViewport", ({ newViewportPosition }) => this.whiteboardRenderer.onMoveViewport(newViewportPosition));

      return this.$q.resolve();
    } else if (loadingTries < limit) {
      loadingTries++;

      return this.$timeout(() => {
        return this.$q.resolve(this.loadMinimap(loadingTries));
      }, delay);
    }
  }

  private attachEventsToWhiteboardRenderer() {
    this.whiteboardRenderer.addEventListener("shapeDragEnd", () => this.whiteboardRenderer.onShapeDragEnd());
    this.whiteboardRenderer.addEventListener("stateChange", (evt) =>
      this.onShapeStateChange(evt.state.id, evt.changes, {
        undo: true,
        notify: evt.notify,
      })
    );
    this.whiteboardRenderer.addEventListener("linkChange", (evt) =>
      this.onShapeLinkStateChange(evt.state, {
        undo: true,
        notify: evt.notify,
      })
    );
    this.whiteboardRenderer.addEventListener("selectionChange", (evt) => {
      const oldShapeId = this.selectedShapes.length === 1 && this.selectedShapes[0].id;

      if (evt.selectedShapes.length === 1) {
        const newShapeId = evt.selectedShapes[0].id;

        // only if one shape was selected on the previous select
        if (oldShapeId && oldShapeId !== newShapeId) {
          this.broadcastUnlockShapeViaSocket(oldShapeId);
        }

        this.whiteboardSocketService.lockShape({ shapeId: newShapeId });
      } else {
        // if more than one shape is selected and there was only one before that, it needs to be unselected
        if (oldShapeId) {
          this.broadcastUnlockShapeViaSocket(oldShapeId);
        }
      }

      this.selectedShapes = evt.selectedShapes;

      this.$timeout(() => {
        this.shapeContextMenuItems = this.selectedShapes.length === 1 ? this.generateShapeContextMenuItems(this.selectedShapes[0]) : [];
      });
    });
    this.whiteboardRenderer.addEventListener("scroll", () => this.renderMinimap());
    this.whiteboardRenderer.addEventListener("startAutoScrolling", (evt) => this.whiteboardRenderer.onStartAutoScrolling(evt));
    this.whiteboardRenderer.addEventListener("stopAutoScrolling", () => this.whiteboardRenderer.onStopAutoScrolling());
  }

  private renderMinimap(): void {
    if (this.minimapRenderer) {
      const state = this.whiteboardStateHolder.exportState();
      this.multiRenderer.renderMinimap(state);
    }
  }

  private broadcastUnlockShapeViaSocket(shapeId: string) {
    // Shape will be missing when publishing it, or converting it to a draft goal
    const shapeExists = !!this.whiteboardStateHolder.findShape(shapeId);
    if (shapeExists) {
      this.whiteboardSocketService.unlockShape({ shapeId });
    }
  }

  private generateShapeContextMenuItems(state: IWhiteboardShapeState): IShapeContextMenuItem[] {
    const menuItems: IShapeContextMenuItem[] = [];
    if ("fontSize" in state) {
      menuItems.push(
        { type: "fontFamily", value: state.fontFamily },
        { type: "fontSize", value: state.fontSize },
        {
          type: "fontStyle",
          bold: state.bold,
          italic: state.italic,
          underline: state.underline,
          strikeThrough: state.strikeThrough,
        }
      );
    }

    this.addTextAlignmentToShapeMenu(menuItems, state);

    if ("textColor" in state) {
      menuItems.push({ type: "textColor", value: state.textColor });
    }

    this.addBackgroundColorToShapeMenu(menuItems, state);

    if (state.type === "heading") {
      menuItems.push({ type: "highlightColor", value: state.highlightColor });
    }

    if (state.type === "goal") {
      menuItems.push({
        type: "link",
        displayName: localize("edit"),
        state: "._goalSidebar.goal",
        stateParams: { id: state.id },
      });
    } else if (state.type === "draftGoal") {
      menuItems.push({
        type: "link",
        displayName: localize("edit"),
        state: ".draftGoal",
        stateParams: { goalId: state.id },
      });
    }

    if (state.type === "note" || state.type === "heading") {
      menuItems.push({
        type: "ghIcon",
        className: "goal",
        tooltip: localize("convert_to_objective"),
        click: () => this.convertSelectedShapeToGoal(),
      });
    }

    if (!(<WhiteboardShapeStateType[]>["alert", "async"]).includes(state.type)) {
      menuItems.push({
        type: "ncIconMini",
        className: "make-copy",
        tooltip: localize("clone"),
        click: () => this.cloneSelectedShape(),
      });
    }

    menuItems.push({
      type: "ncIconMini",
      className: "trash-bin",
      tooltip: localize("delete"),
      click: () => this.deleteSelectedShapes(),
    });

    const [isSendToBackAllowed, isBringToFrontAllowed] = this.getZActionsAvailability(state);
    const maxZIndex = this.whiteboardStateHolder.calculateNextZIndex();
    menuItems.push({ type: "threeDots", value: state.zIndex, isSendToBackAllowed, isBringToFrontAllowed, maxZIndex });

    return menuItems;
  }

  private getZActionsAvailability(menuItem: IWhiteboardShapeState): [boolean, boolean] {
    const [minZIndex, maxZIndex] = this.whiteboardStateHolder.getMinMaxZIndices();
    const menuItemZIndex = menuItem.zIndex;

    const isSendToBackAllowed = menuItemZIndex > minZIndex;
    const isBringToFrontAllowed = menuItemZIndex < maxZIndex;

    return [isSendToBackAllowed, isBringToFrontAllowed];
  }

  private addBackgroundColorToShapeMenu(menuItems: IShapeContextMenuItem[], state: IWhiteboardShapeState) {
    if (this.context.whiteboardBasicShapesEnabled) {
      if (
        state.type === "note" ||
        state.type === "rectangle" ||
        state.type === "ellipse" ||
        state.type === "rhombus" ||
        state.type === "rounded-rectangle" ||
        state.type === "parallelogram"
      ) {
        menuItems.push({ type: "backgroundColor", value: state.backgroundColor, valueToolbar: state.backgroundColor });
      }
    } else {
      if (state.type === "note") {
        menuItems.push({
          type: "backgroundColor",
          value: state.backgroundColor,
          valueToolbar: colors.whiteboardColors[state.backgroundColor],
        });
      }
    }
  }

  private addTextAlignmentToShapeMenu(menuItems: IShapeContextMenuItem[], state: IWhiteboardShapeState) {
    if ("textAlignment" in state) {
      if (state.type === "note") {
        menuItems.push({ type: "textAlignment", value: state.textAlignment });
      } else {
        menuItems.push({ type: "alignment", value: state.textAlignment });
      }
    }
  }

  private loadWhiteboard(): IPromise<IWhiteboardState> {
    return this.whiteboardsService.getById(this.whiteboardId).then((whiteboard) => {
      this.context.reactionSettings = whiteboard.reactionSettings;
      const { state } = whiteboard;

      delete whiteboard.state;

      this.analyticsService.track("Whiteboard Opened", { id: whiteboard.id, owner_id: whiteboard.ownerId });
      this.whiteboard = whiteboard;

      const shapesZIndicesAssigned = state.shapes.reduce((acc, x) => acc + x.zIndex || 0, 0);

      if (!shapesZIndicesAssigned) {
        state.shapes.forEach((shape, index) => {
          shape.zIndex = index;
        });
      }

      this.viewHistoryService.addItemToViewHistory(
        this.viewHistoryService.payloadBuilder.withWhiteboard(whiteboard).forSource("navigation").generate(),
        navItemsFactory.withRandomColor(navItemsFactory.buildWhiteboardNavItem(whiteboard))
      );

      // start loading fonts used in shapes, but even if they fail for some reason,
      // we will still render the shapes with a fallback font
      const shapeFonts = unique(state.shapes.reduce<string[]>((acc, shape) => ("fontFamily" in shape ? acc.concat(shape.fontFamily) : acc), []));
      return this.fontDownloader
        .loadFonts(...shapeFonts)
        .catch(noop)
        .then(() => state);
    });
  }

  private render(loadingPromise: IPromise<IWhiteboardState>): IPromise<void> {
    return loadingPromise.then((state) => {
      this.changeTracker = new ChangeHistoryTracker(this.whiteboardStateHolder, this.bufferedStateChangeNotifier);
      this.stateProcessor = new StateProcessor(
        this.changeTracker,
        this.whiteboardStateHolder,
        this.shapeStateConverter,
        this.userService,
        this.sessionsIdMap,
        this.alignmentRejectionModal,
        this.uiErrorHandlingService
      );

      let associatedLinks = this.replaceExistingShapesWithAsyncOnes(state);

      this.whiteboardStateHolder.load(this.whiteboard, state);

      this.onDestroy(() => {
        this.syncStateChangesAndUpdatePicture();
        this.whiteboardStateHolder.unload();
      });

      // give Angular.js a chance to update the view before initializing the renderer
      this.$timeout(() => {
        this.initRenderer().then(() => {
          // when async states are resolved we replace them with their resolved shape state
          const asyncShapes = state.shapes.filter((shape) => shape.type === "async") as IAsyncState[];
          const originalShapesMap = toIdMap(asyncShapes.map((s) => s.original));

          for (const asyncShape of asyncShapes) {
            asyncShape.promise.then((resolvedShape) => {
              if (!resolvedShape) {
                this.changeTracker.beginTransaction("Delete async shapes for unloaded goal").deleteShape(asyncShape).commit({
                  undo: false,
                  notify: false,
                });
              } else {
                delete originalShapesMap[asyncShape.original.id];

                const linkFromOrToAsyncShapeWithOtherResolvedShapes = (link: IWhiteboardLinkState) =>
                  (link.source.id === resolvedShape.id && !originalShapesMap[link.target.id]) ||
                  (link.target.id === resolvedShape.id && !originalShapesMap[link.source.id]);

                const [resolvedShapeLinks, otherLinks] = splitBy(associatedLinks, linkFromOrToAsyncShapeWithOtherResolvedShapes);
                associatedLinks = otherLinks;

                this.changeTracker
                  .beginTransaction("Load goal")
                  .deleteShape(asyncShape)
                  .updateShape(resolvedShape)
                  .updateLink(...resolvedShapeLinks)
                  .commit({ undo: false, notify: false });
              }

              this.publishUnsyncedStateChanges();
              this.whiteboardStateHolder.render(this.multiRenderer);
            });
          }

          if (!asyncShapes.length) {
            this.publishUnsyncedStateChanges();
            this.whiteboardStateHolder.render(this.multiRenderer);
          }
        });
      });
    });
  }

  private replaceExistingShapesWithAsyncOnes(state: IWhiteboardState): IWhiteboardLinkState[] {
    return this.stateProcessor.replaceExistingShapesWithAsyncOnes(state);
  }

  private onForeignStateChange(args: IStateChangeSocketMessage): void {
    const { payload } = args;
    if (Array.isArray(payload)) {
      let shapeId: string;

      const stateTransaction = this.changeTracker.beginTransaction("On Foreign State Change");

      for (const change of payload) {
        if (change.path.includes("shapes") && "id" in payload[0].value) {
          shapeId = payload[0].value.id;
        }

        switch (change.op) {
          case "add":
            onStateChangeAddShape(change, stateTransaction);
            break;
          case "replace":
            onStateChangeAddShape(change, stateTransaction);
            break;
          case "remove":
            if (change.path.includes("shapes")) {
              delete this.lockedShapesIdMap[(change.value as IWhiteboardShapeState).id];
            }

            onStateChangeRemoveShape(change, stateTransaction);
            break;
        }
      }

      stateTransaction.commit({ undo: false, notify: false });
      this.whiteboardStateHolder.render(this.multiRenderer, { keepCurrentViewport: true, shapeId });
    }

    this.updateShapesWithBsonIds(payload);

    if ((payload as IStateChangeSuccess).status === "success") {
      this.bufferedStateChangeNotifier.shiftUnsyncedStateChange();
    }
  }

  private updateShapesWithBsonIds(payload: IWhiteboardStateChange[] | IStateChangeSuccess) {
    if ("addedShapeIds" in payload) {
      const shapeIdsMap = (payload as IStateChangeSuccess).addedShapeIds;
      Object.keys(shapeIdsMap).forEach((shapeId: string) => {
        this.stateProcessor.updateShape(shapeId, { bsonId: shapeIdsMap[shapeId] }, { undo: false, notify: false });
        this.whiteboardStateHolder.renderSingleShape(this.multiRenderer, shapeId, { keepCurrentViewport: true });
      });
    }
  }

  private publishUnsyncedStateChanges(): void {
    /*
      Unsynced state changes - These are all the changes which current user made.
      We want to keep these changes until we sync/save them in the DB. In the best case, this happens immediately via socket.
      If there is a “success” message in the socket we remove one change from the array.
      If we can’t sync changes via socket, on destroy we will try to sync changes via an HTTP request.
      The idea is the current user to never lose his changes, of course, if he has internet :)
    */
    const unsyncedStateChanges = this.bufferedStateChangeNotifier.unsyncedStateChanges;

    if (!unsyncedStateChanges.length) {
      return;
    }

    this.bufferedStateChangeNotifier.clearUnsyncedStateChanges();
    publishUnsyncedStateChanges(this.changeTracker, flatten(unsyncedStateChanges), { batch: 5 });
  }

  private onShapeStateChange(shapeId: string, changes: Record<string, unknown>, options: { undo: boolean; notify: boolean }) {
    const updatedShape = this.whiteboardStateHolder.findShape(shapeId);
    const movedItem = changes.movedItem as IGoalChildReorderPayload;
    if (updatedShape.type === "goal" && movedItem) {
      this.goalService.reorderGoalChild(movedItem).then((res) => {
        const metricId = movedItem.moved.id;
        const metrics = updatedShape.metrics.map((m) => {
          if (m.id === metricId) {
            m.orderId = res.orderId;
          }
          return m;
        });

        this.updateShape(shapeId, { metrics: metrics }, options);
      });

      return;
    }

    this.updateShape(shapeId, changes, options);
  }

  private onShapeLinkStateChange(link: IWhiteboardLinkState, options: { undo: boolean; notify: boolean }) {
    this.stateProcessor.updateLink(link, options);
    this.whiteboardStateHolder.render(this.multiRenderer);
  }

  private updateShape(shapeId: string, changes: Record<string, unknown>, options: { undo: boolean; notify: boolean }) {
    this.stateProcessor.updateShape(shapeId, changes, options);
    this.reRenderAndUpdateSelectedShape(shapeId);
  }

  private reRenderAndUpdateSelectedShape(shapeId: string) {
    this.whiteboardStateHolder.render(this.multiRenderer);
    const updatedShape = this.whiteboardStateHolder.findShape(shapeId);

    const index = this.selectedShapes.findIndex((s) => s.id === updatedShape.id);
    if (index >= 0) {
      this.selectedShapes[index] = updatedShape;
    }

    if (this.selectedShapes.length === 1 && index === 0) {
      this.shapeContextMenuItems = this.generateShapeContextMenuItems(updatedShape);
    }
  }

  private onGoalsChanged(args: GoalsChangedEventArgs): void {
    this.stateProcessor.onGoalsChanged(args, this.whiteboardRenderer, this.onShapeStateChange.bind(this));
  }

  private getCurrentUserAllowedActions(): IPromise<void> {
    return this.permissionsService.getCurrentUserAllowedActionsPerItem(this.whiteboardId, "whiteboard").then((actions) => {
      this.currentUserAllowedActions = actions.currentUserActions as (keyof IAllowedActionsMap)[];
      this.context.readonly = !this.currentUserAllowedActions.includes("update");
    });
  }

  private onCollaborate(args: ICollaborateWhiteboardSocketMessage): void {
    this.processCollaborators(args.payload.clients, args.connectionId);
    this.processLockedShapesOnCollaborate(args.payload.lockedShapes);

    // trigger scope apply, so that changes are immediately passed to the header
    this.$scope.$apply();
  }

  private processCollaborators(clientsPayload: { clientId: string; connectionId: string }[], connectionId: string): void {
    const currentUserId = getCurrentUserId();

    for (const client of clientsPayload) {
      const collaborator = {
        ...client,
        color: getRandomColor(),
      };

      if (currentUserId === client.clientId && client.connectionId === connectionId) {
        this.currentUserConnectionId = client.connectionId;
      }

      if (!this.collaborators) {
        this.collaborators = {};
        this.collaborators[client.connectionId] = collaborator;
      } else {
        if (!this.collaborators[client.connectionId]) {
          this.collaborators[client.connectionId] = collaborator;
        }
      }

      for (const collaborator of Object.values(this.collaborators)) {
        if (!clientsPayload.find((client) => client.connectionId === collaborator.connectionId)) {
          delete this.collaborators[collaborator.connectionId];
        }
      }
    }
  }

  private processLockedShapesOnCollaborate(lockedShapesIdMapPayload: { [shapeId: string]: string }): void {
    // add/replace locked shapes in the map
    for (const [shapeId, connectionId] of Object.entries(lockedShapesIdMapPayload)) {
      this.lockedShapesIdMap[shapeId] = connectionId;

      if (connectionId !== this.currentUserConnectionId) {
        this.whiteboardRenderer.lockShapeById(shapeId, this.getCollaboratorLockedShapeConfig(connectionId));
      }
    }

    // remove the onces that are no longer locked
    const lockedShapesIdMapValues = Object.values(lockedShapesIdMapPayload);
    for (const [shapeId, connectionId] of Object.entries(this.lockedShapesIdMap)) {
      if (!lockedShapesIdMapValues.includes(connectionId)) {
        this.whiteboardRenderer.unlockShapes(shapeId);
        delete this.lockedShapesIdMap[shapeId];
      }
    }
  }

  private getCollaboratorLockedShapeConfig(collaboratorId: string): { color: string; userFirstName: string } {
    if (!this.collaborators) {
      console.warn("Haven't received collaborators event yet");
      return { color: "#6236ff", userFirstName: "" };
    }

    const collaborator = this.collaborators[collaboratorId];

    if (!collaborator) {
      console.warn("Trying to get locked shapes for user that isn't part of collaborators");
    }

    const user = collaborator && this.assigneeIdMap[collaborator.clientId];
    return { color: collaborator ? collaborator.color : "#6236ff", userFirstName: user ? user.name.split(" ")[0] : "" };
  }

  private syncStateChangesAndUpdatePicture(): void {
    const unsyncedStateChanges = flatten(this.bufferedStateChangeNotifier.unsyncedStateChanges);

    if (!unsyncedStateChanges.length) {
      if (!this.whiteboard.picture) {
        this.whiteboardsService.updatePicture(this.whiteboard, this.canvasDataUrl()).catch((error) => console.error(error));
      }
      return;
    }

    this.$q
      .all([
        this.whiteboardsService.applyStateChanges(this.whiteboardId, unsyncedStateChanges),
        this.whiteboardsService.updatePicture(this.whiteboard, this.canvasDataUrl()),
      ])
      .catch((error) => console.error(error));
  }

  private hoverAssigneeIds(ids: string[]): void {
    this.$timeout(() => (this.hoverIds = ids));
  }

  private onShapeReaction(payload: IShapeReactionResponse) {
    const shape = this.whiteboardStateHolder.findShape((s) => s.bsonId === payload.targetId) as INoteState | IDraftGoalState;
    shape.reactions = payload.reactions;
    this.whiteboardStateHolder.updateShape(shape);
  }

  private onUnlockSelectedShapes(shapes: WhiteboardShape<IWhiteboardShapeState>[]): void {
    shapes.forEach((sh) => this.broadcastUnlockShapeViaSocket(sh.state.id));
  }

  private setDefaultSession(): IPromise<string> {
    const newDefaultSessionId = getClosestOpenFutureSession(this.sessions, this.currentUser)?.id || getClosestOpenActiveSession(this.sessions, this.currentUser)?.id;

    if (!newDefaultSessionId) {
      return this.$q.resolve("");
    }

    return this.whiteboardsService.update(this.whiteboard.id, { defaultSessionId: newDefaultSessionId }).then(
      () => {
        this.onDefaultSessionChanged({ defaultSessionId: newDefaultSessionId });
        return newDefaultSessionId;
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
        return "";
      }
    );
  }
}

export const WhiteboardComponent: IComponentOptions = {
  controller: WhiteboardComponentCtrl,
  templateUrl: require("./whiteboard.jade"),
  bindings: {
    whiteboardId: "@",
  },
};
